/**
 * ConnectionsStep Helper Functions
 */

import * as yup from "yup";
import type {
    Tracker,
    MarketplaceIds,
    MMPProvider,
    StoreType,
    ChangeDetectionResult,
} from "./ConnectionsStep.types";
import { TRACKER_EXTRA_KEYS } from "./ConnectionsStep.constants";

/**
 * Extracts marketplace app IDs from tracker sources based on MMP provider
 * 
 * @param tracker - Selected tracker object containing sources
 * @param mmpProvider - Selected MMP provider ('adjust' | 'appsflyer' | 'none')
 * @returns Object with ios and android app IDs extracted from tracker sources
 * 
 * @example
 * const tracker = { sources: [...] };
 * const ids = extractMarketplaceIds(tracker, 'appsflyer');
 * // Returns: { ios: 'id123456789', android: 'com.example.app' }
 */
export const extractMarketplaceIds = (
    tracker: Tracker | undefined,
    mmpProvider: MMPProvider | ""
): MarketplaceIds => {
    // Return empty if no tracker, no MMP, or MMP is "none"
    if (!tracker?.sources || !mmpProvider || mmpProvider === "none") {
        return { ios: null, android: null };
    }

    const result: MarketplaceIds = { ios: null, android: null };

    // Find sources that match the selected MMP provider
    const mmpSources = tracker.sources.filter(
        (source) =>
            source?.source_type?.code?.toLowerCase() === mmpProvider.toLowerCase()
    );

    // Extract app IDs from extras for each matching source
    mmpSources.forEach((source) => {
        if (!source?.extras || !Array.isArray(source.extras)) return;

        const platformExtra = source.extras.find(
            (extra) => extra?.key === TRACKER_EXTRA_KEYS.PLATFORM
        );
        const appIdExtra = source.extras.find(
            (extra) => extra?.key === TRACKER_EXTRA_KEYS.APP_ID
        );

        if (platformExtra?.value && appIdExtra?.value) {
            const platform = platformExtra.value.toLowerCase();
            if (platform === "ios") {
                result.ios = appIdExtra.value;
            } else if (platform === "android") {
                result.android = appIdExtra.value;
            }
        }
    });

    return result;
};

/**
 * Detects what has changed between previous and current state
 */
export const detectChanges = (
    prevTrackerId: number | null,
    currentTrackerId: number | null,
    prevMmp: MMPProvider | "",
    currentMmp: MMPProvider | "",
    prevAppTypes: StoreType[] | undefined,
    currentAppTypes: StoreType[] | undefined
): ChangeDetectionResult => {
    const trackerChanged = prevTrackerId !== currentTrackerId;
    const mmpChanged = prevMmp !== currentMmp;

    // Check if app types changed (specifically if apple or android was added)
    const prevHadApple = prevAppTypes?.includes("apple") ?? false;
    const prevHadAndroid = prevAppTypes?.includes("android") ?? false;
    const nowHasApple = currentAppTypes?.includes("apple") ?? false;
    const nowHasAndroid = currentAppTypes?.includes("android") ?? false;
    const appleAdded = !prevHadApple && nowHasApple;
    const androidAdded = !prevHadAndroid && nowHasAndroid;

    return {
        trackerChanged,
        mmpChanged,
        appleAdded,
        androidAdded,
        hasRelevantChanges: trackerChanged || mmpChanged || appleAdded || androidAdded,
    };
};

/**
 * ConnectionsStep Validation Schema
 * 
 */
export const connectionsStepSchema = yup.object().shape({
    tracker_id: yup.number().required("Tracker is required"),
    mmp: yup.string().required("MMP Provider is required"),
    one_link: yup.string().nullable(),
    app_type: yup.array().of(yup.string()),
    applications: yup.object().shape({
        apple: yup.string().nullable(),
        android: yup.string().nullable(),
        phone_number: yup.string().nullable(),
    }),
}).test(
    'apple-app-id-required',
    'Apple App Store ID is required when Apple store type is selected',
    function (values) {
        const { app_type, applications } = values;
        if (app_type?.includes('apple') && !applications?.apple?.trim()) {
            return this.createError({
                path: 'applications.apple',
                message: 'Apple App Store ID is required',
            });
        }
        return true;
    }
).test(
    'android-app-id-required',
    'Android App Package Name is required when Android store type is selected',
    function (values) {
        const { app_type, applications } = values;
        if (app_type?.includes('android') && !applications?.android?.trim()) {
            return this.createError({
                path: 'applications.android',
                message: 'Android App Package Name is required',
            });
        }
        return true;
    }
).test(
    'phone-number-required',
    'Phone number is required when WhatsApp store type is selected',
    function (values) {
        const { app_type, applications } = values;
        if (app_type?.includes('whatsapp') && !applications?.phone_number?.trim()) {
            return this.createError({
                path: 'applications.phone_number',
                message: 'Phone number is required for WhatsApp',
            });
        }
        return true;
    }
);

/**
 * Default form values for ConnectionsStep
 */
export const getDefaultFormValues = (form?: Partial<Record<string, unknown>>) => ({
    tracker_id: null,
    mmp: "",
    ...form,
});

